'use client';
import React, { useMemo, useRef, useState, useTransition } from 'react';
import { useLocale } from 'use-intl';
import {
  ChevronLeftSIcon,
  ChevronRightSIcon,
  CloseIcon,
  PlayCircleStrokeIcon,
} from 'tdesign-icons-react';
import Image from '../../../image';
import SectionTitle from './SectionTitle';
import SectionYearSelector from './SectionYearSelector';
import styles from './VideosSection.module.css';
import { fetchConferenceVideosByYear } from '../api';
import type {
  ConferenceSite,
  ConferenceVideoItem,
  ConferenceVideoListResp,
} from '../types';

interface VideosSectionProps {
  data?: ConferenceVideoListResp;
  title?: string;
  site?: ConferenceSite;
  years?: number[];
}

const DESKTOP_VIDEO_PAGE_SIZE = 6;
const MOBILE_VIDEO_PAGE_SIZE_ZH = 6;
const MOBILE_VIDEO_PAGE_SIZE_EN = 6;
const MOBILE_PAGER_TRACK_WIDTH_REM = 1.5;

const chunkList = <T,>(list: T[], size: number) => {
  const pages: T[][] = [];
  for (let index = 0; index < list.length; index += size) {
    pages.push(list.slice(index, index + size));
  }
  return pages;
};

const getVideoTitle = (item: ConferenceVideoItem, isZh: boolean) =>
  (isZh ? item.TitleCN || item.TitleEN : item.TitleEN || item.TitleCN) || '-';

const getVideoTag = (item: ConferenceVideoItem, isZh: boolean) =>
  isZh ? item.TagCN || item.TagEN : item.TagEN || item.TagCN;

const getExternalVideoURL = (item: ConferenceVideoItem) => {
  if (item.VideoSource !== 2 && item.PlayType !== 'external') {
    return '';
  }
  return item.PlayURL || item.VideoURL || '';
};

function VideoPlayIcon() {
  const filterId = React.useId();

  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="15" cy="15" r="15" fill="black" fillOpacity="0.6" />
      <g filter={`url(#${filterId})`}>
        <path
          d="M12.3256 15L12.3256 11.1766C12.3256 10.0973 13.5615 9.48498 14.4202 10.1388L19.4411 13.9623C20.1266 14.4843 20.1266 15.5157 19.4411 16.0377L14.4202 19.8612C13.5615 20.515 12.3256 19.9027 12.3256 18.8234L12.3256 15Z"
          fill="white"
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="9.71687"
          y="8.56553"
          width="12.847"
          height="15.4776"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1.30435" />
          <feGaussianBlur stdDeviation="1.30435" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

/**
 * 精彩视频组件
 */
export default function VideosSection({
  data,
  title,
  site = 'zh',
  years,
}: VideosSectionProps) {
  const locale = useLocale();
  const isZh = locale === 'zh';
  const [, startTransition] = useTransition();
  const requestIdRef = useRef(0);
  const [sectionData, setSectionData] = useState<ConferenceVideoListResp>(
    data || { list: [], total: 0, page: 1, pageSize: 100 }
  );
  const [selectedYear, setSelectedYear] = useState(
    () =>
      years?.[0] ||
      data?.list?.find((item) => item.ConferenceYear)?.ConferenceYear ||
      new Date().getFullYear()
  );
  const list = useMemo(() => sectionData?.list || [], [sectionData]);
  const [activeVideo, setActiveVideo] = useState<ConferenceVideoItem | null>(
    null
  );
  const [desktopPage, setDesktopPage] = useState(1);
  const [mobileScrollLeft, setMobileScrollLeft] = useState(0);
  const mobilePagerRef = useRef<HTMLDivElement>(null);
  const hasVideos = list.length > 0;
  const mobileVideoPageSize = isZh
    ? MOBILE_VIDEO_PAGE_SIZE_ZH
    : MOBILE_VIDEO_PAGE_SIZE_EN;
  const desktopPageCount = Math.max(
    1,
    Math.ceil(list.length / DESKTOP_VIDEO_PAGE_SIZE)
  );
  const safeDesktopPage = Math.min(desktopPage, desktopPageCount);
  // PC 端按 4 列 x 2 行分页展示，接口仍一次拉取当前届次全部视频配置，避免切页反复请求。
  const desktopVideoList = useMemo(() => {
    const start = (safeDesktopPage - 1) * DESKTOP_VIDEO_PAGE_SIZE;
    return list.slice(start, start + DESKTOP_VIDEO_PAGE_SIZE);
  }, [list, safeDesktopPage]);
  // 移动端仍按每页最多 6 条分页，但页内改为两列独立纵向堆叠，避免不同行文案高度互相撑高。
  const mobileVideoPages = useMemo(
    () => chunkList(list, mobileVideoPageSize),
    [list, mobileVideoPageSize]
  );
  const yearOptions = useMemo(() => years || [], [years]);

  React.useEffect(() => {
    setSectionData(data || { list: [], total: 0, page: 1, pageSize: 100 });
    if (years?.[0]) {
      setSelectedYear(years[0]);
    }
  }, [data, years]);

  React.useEffect(() => {
    setDesktopPage(1);
    setMobileScrollLeft(0);
    mobilePagerRef.current?.scrollTo({ left: 0 });
    setActiveVideo(null);
  }, [list]);

  const handleYearChange = (year: number) => {
    if (year === selectedYear) {
      return;
    }
    setSelectedYear(year);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    startTransition(() => {
      void fetchConferenceVideosByYear({
        conferenceYear: year,
        site,
      }).then((nextData) => {
        if (requestIdRef.current === requestId) {
          setSectionData(nextData);
        }
      });
    });
  };

  const getMobileIndicatorLeft = () => {
    const pager = mobilePagerRef.current;
    if (!pager) {
      return 0;
    }

    const scrollableWidth = pager.scrollWidth - pager.clientWidth;
    if (scrollableWidth <= 0) {
      return -1;
    }

    const indicatorWidthRem =
      MOBILE_PAGER_TRACK_WIDTH_REM / mobileVideoPages.length;
    const maxOffsetRem = MOBILE_PAGER_TRACK_WIDTH_REM - indicatorWidthRem;
    const ratio = Math.min(1, Math.max(0, mobileScrollLeft / scrollableWidth));
    return maxOffsetRem * ratio;
  };

  const handleMobilePagerScroll = () => {
    const pager = mobilePagerRef.current;
    if (!pager) {
      return;
    }
    setMobileScrollLeft(pager.scrollLeft);
  };

  const handleVideoCardClick = (item: ConferenceVideoItem) => {
    const externalURL = getExternalVideoURL(item);
    if (externalURL) {
      // 外部视频链接按 PRD 直接新开标签页，避免 PC/移动端在站内弹窗嵌套第三方页面导致播放异常。
      window.open(externalURL, '_blank', 'noopener,noreferrer');
      return;
    }
    setActiveVideo(item);
  };

  const handleDesktopPageChange = (nextPage: number) => {
    if (
      nextPage < 1 ||
      nextPage > desktopPageCount ||
      nextPage === safeDesktopPage
    ) {
      return;
    }
    setDesktopPage(nextPage);
  };

  const renderVideoCard = (item: ConferenceVideoItem) => {
    const titleText = getVideoTitle(item, isZh);
    const tagText = getVideoTag(item, isZh);
    return (
      <div
        key={item.VideoID || item.ID}
        className={styles.videoCard}
        onClick={() => handleVideoCardClick(item)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleVideoCardClick(item);
          }
        }}
      >
        <div className={styles.coverWrap}>
          {item.CoverImage ? (
            <Image
              src={item.CoverImage}
              alt={titleText}
              className={styles.cover}
              loading="lazy"
            />
          ) : (
            <div className={styles.coverFallback}>-</div>
          )}
          <span className={styles.playButton}>
            <VideoPlayIcon />
          </span>
          {tagText && (
            <span className={styles.videoTag} title={tagText}>
              {tagText}
            </span>
          )}
        </div>
        <div className={styles.videoBody}>
          <h3>{titleText}</h3>
        </div>
      </div>
    );
  };

  const splitMobilePageColumns = (pageVideos: ConferenceVideoItem[]) => {
    const leftColumn: ConferenceVideoItem[] = [];
    const rightColumn: ConferenceVideoItem[] = [];

    pageVideos.forEach((item, index) => {
      if (index % 2 === 0) {
        leftColumn.push(item);
        return;
      }
      rightColumn.push(item);
    });

    return [leftColumn, rightColumn];
  };

  const mobileIndicatorLeft = getMobileIndicatorLeft();

  // 无数据时仍保留模块标题和锚点，保证顶部导航可以跳转到精彩视频模块。
  return (
    <section
      id="videos"
      className={`${styles.section} ${isZh ? '' : styles.sectionEn}`}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <SectionTitle className={styles.sectionTitle}>{title}</SectionTitle>
          <SectionYearSelector
            selectedYear={selectedYear}
            years={yearOptions}
            onChange={handleYearChange}
          />
        </div>
        {hasVideos ? (
          <>
            <div
              className={`${styles.videoGrid} ${
                desktopPageCount > 1 ? styles.videoGridPaged : ''
              }`}
            >
              {desktopVideoList.map(renderVideoCard)}
            </div>
            {desktopPageCount > 1 && (
              <div
                className={styles.desktopPager}
                aria-label={isZh ? '精彩视频翻页' : 'Video pagination'}
              >
                <button
                  className={styles.pageButton}
                  disabled={safeDesktopPage === 1}
                  onClick={() => handleDesktopPageChange(safeDesktopPage - 1)}
                  type="button"
                  aria-label={isZh ? '上一页' : 'Previous page'}
                >
                  <ChevronLeftSIcon size={24} />
                </button>
                <span className={styles.pageText}>
                  {safeDesktopPage}/{desktopPageCount}
                </span>
                <button
                  className={styles.pageButton}
                  disabled={safeDesktopPage === desktopPageCount}
                  onClick={() => handleDesktopPageChange(safeDesktopPage + 1)}
                  type="button"
                  aria-label={isZh ? '下一页' : 'Next page'}
                >
                  <ChevronRightSIcon size={24} />
                </button>
              </div>
            )}
            <div
              ref={mobilePagerRef}
              className={styles.mobileVideoPager}
              onScroll={handleMobilePagerScroll}
            >
              {mobileVideoPages.map((pageVideos, pageIndex) => (
                <div className={styles.mobileVideoPage} key={pageIndex}>
                  {splitMobilePageColumns(pageVideos).map(
                    (columnVideos, columnIndex) => (
                      <div
                        className={styles.mobileVideoColumn}
                        key={`mobile-column-${pageIndex}-${columnIndex}`}
                      >
                        {columnVideos.map(renderVideoCard)}
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
            {mobileVideoPages.length > 1 && mobileIndicatorLeft >= 0 && (
              <div className={styles.mobilePageIndicator} aria-hidden="true">
                <div className={styles.mobilePageIndicatorTrack}>
                  <span
                    className={styles.mobilePageIndicatorThumb}
                    style={{
                      left: `${mobileIndicatorLeft}rem`,
                      width: `${
                        MOBILE_PAGER_TRACK_WIDTH_REM / mobileVideoPages.length
                      }rem`,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.empty}>{isZh ? '暂无视频' : 'No videos'}</div>
        )}
      </div>
      {activeVideo && (
        <div className={styles.modalMask} onClick={() => setActiveVideo(null)}>
          <div
            className={styles.modalPanel}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={() => setActiveVideo(null)}
              type="button"
              aria-label={isZh ? '关闭' : 'Close'}
            >
              <CloseIcon size={22} />
            </button>
            <div className={styles.modalTitle}>
              <PlayCircleStrokeIcon size={22} />
              <span>{getVideoTitle(activeVideo, isZh)}</span>
            </div>
            {activeVideo.PlayURL ? (
              activeVideo.VideoSource === 2 ? (
                <iframe
                  className={styles.videoFrame}
                  src={activeVideo.PlayURL}
                  title={getVideoTitle(activeVideo, isZh)}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  className={styles.videoPlayer}
                  src={activeVideo.PlayURL}
                  poster={activeVideo.CoverImage}
                  controls
                  playsInline
                />
              )
            ) : (
              <div className={styles.videoEmpty}>-</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
